import React, { useState, memo } from "react";
import * as S from "./styled";
import CameraIcon from "_c/icon/Camera";
import ShareIcon from "_c/icon/Share";
import ResponseForm from "_c/response-form/index";
import { generateUrl } from "@/utils/cloudinary";
import { normalizeRating } from "@/utils";
import moment from "moment";
import { Link } from "react-router-dom";
import MetaCounter from "_c/MetaCounter";
import CommentIcon from "_c/icon/Comment";
import StarIcon from "_c/icon/Star";
import Comments from "./Comments";
import { OvalPreloader } from "_c/preloader";
import PT from "prop-types";
import { useCloudinaryWidget } from "@/hooks/useCloudinaryWidget";
import { LinkGenerator } from "@/utils/LinkGenerator";
import size from "lodash/size";
import { SITE_URL } from "@/misc/constants";

const Review = ({ review, openShareModal, t, ...props }) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const { uploadPhoto } = useCloudinaryWidget();
  const [loadingComments, setLoadingComments] = useState(false);
  const [isShowComments, setIsShowComments] = useState(false);
  const author = review.client;
  const authorLink = LinkGenerator.clientProfile(author._id);
  const openComments = async () => {
    if (size(review.comments) < review.comments_count) {
      setLoadingComments(true);
      try {
        await props.loadComments(review._id);
      } catch (e) {
        // TODO:: handle error
        console.error(e);
      } finally {
        setLoadingComments(false);
      }
    }
    setIsShowComments(true);
  };
  const onPhotoChange = async () => {
    const photo = await uploadPhoto();
    if (photo) {
      props.updateReview(review._id, {
        photo,
      });
    }
  };
  const onShare = () => {
    openShareModal(SITE_URL + LinkGenerator.review(review._id));
  };
  const addComment = (payload) => {
    props.addComment(review._id, payload);
    setShowResponseForm(false);
  };
  const closeComments = () => {
    setIsShowComments(false);
  };
  const removeComment = (id) => {
    props.removeComment(review._id, id);
  };
  return (
    <React.Fragment>
      <S.Container>
        <S.Meta />
        <S.InnerWrap>
          <S.AvatarWrap>
            <Link to={authorLink}>
              <S.Avatar size="lg" avatar={author.avatar} sex={author.sex} />
            </Link>
            <S.Rate>{normalizeRating(review.rate)}</S.Rate>
          </S.AvatarWrap>
          <S.Sect>
            <S.Name to={authorLink}>
              {[author.first_name, author.last_name].filter(Boolean).join(" ")}
            </S.Name>
            <S.When>{moment(review.created_at).calendar()}</S.When>
            <div>
              {review.master && (
                <S.ProviderLink to={LinkGenerator.masterProfile(review.master)}>
                  {t("common:master")}
                </S.ProviderLink>
              )}
              {review.saloon && (
                <S.ProviderLink to={LinkGenerator.saloonProfile(review.saloon)}>
                  {t("common:salon")}
                </S.ProviderLink>
              )}
            </div>
            <S.Quote>{review.text}</S.Quote>
            {review.photo && (
              <S.MobileFigure>
                {review.photo && <S.Photo src={generateUrl(review.photo)} />}
              </S.MobileFigure>
            )}
            <S.ButtonSect>
              {props.isAuth && (
                <S.RespBtn
                  onClick={() => setShowResponseForm(!showResponseForm)}
                >
                  {t("common:respond")}
                </S.RespBtn>
              )}
              <S.MetaButtonSect>
                {props.isAuthor && !props.hidePhotoButton && (
                  <S.CameraBtn onClick={onPhotoChange}>
                    <CameraIcon color="#ffffff" />
                  </S.CameraBtn>
                )}
                <S.ShareBtn onClick={onShare}>
                  <ShareIcon color="var(--color-primary)" />
                </S.ShareBtn>
                <MetaCounter count={review.comments_count}>
                  <CommentIcon color="var(--color-primary)" />
                </MetaCounter>
                <MetaCounter
                  onClick={
                    props.isAuth ? () => props.toggleLike(review._id) : null
                  }
                  count={review.likes_count}
                >
                  <StarIcon
                    filled={props.isFavorite}
                    color="var(--color-primary)"
                  />
                </MetaCounter>
              </S.MetaButtonSect>
            </S.ButtonSect>
          </S.Sect>
          {review.photo && (
            <S.Figure>
              <S.Photo src={generateUrl(review.photo)} />
            </S.Figure>
          )}
        </S.InnerWrap>
        {review.comments_count !== 0 && (
          <S.OpenComments
            disabled={loadingComments}
            onClick={isShowComments ? closeComments : openComments}
          >
            <span>
              {loadingComments ? (
                <OvalPreloader style={{ width: "18px" }} />
              ) : (
                `${t("common:comments")} (${review.comments_count})`
              )}
            </span>
          </S.OpenComments>
        )}
      </S.Container>
      <ResponseForm
        onClose={() => setShowResponseForm(false)}
        allowPhoto
        t={t}
        isOpen={showResponseForm}
        onSubmit={addComment}
      />
      {isShowComments && (
        <Comments
          t={t}
          authUserId={props.authUserId}
          reviewId={review._id}
          addComment={addComment}
          reviewMasterId={review.master}
          reviewClientId={author._id}
          comments={review.comments}
          removeComment={removeComment}
        />
      )}
    </React.Fragment>
  );
};
Review.displayName = "Review";
Review.propTypes = {
  isAuthor: PT.bool,
  isAuth: PT.bool,
  review: PT.object,
  isFavorite: PT.bool,
  authUserId: PT.string,
  hidePhotoButton: PT.bool,

  t: PT.func.isRequired,
  updateReview: PT.func.isRequired,
  toggleLike: PT.func.isRequired,
  addComment: PT.func.isRequired,
  removeComment: PT.func.isRequired,
  loadComments: PT.func.isRequired,
};

export default memo(Review);
